import React, { useContext, useEffect, useState } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { ImFilesEmpty } from "react-icons/im";
import axios from "axios";
import { BaseURL } from "../../../../Files/BaseURL";
import AlertContext from "../../../../Context/Alert/AlertContext";
import { useNavigate } from "react-router-dom";

function UploadMissing() {
  const [Message, setMessage] = useState("");
  const [pendingDocs, setPendingDocs] = useState([]);
  const [fileStates, setFileStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPending, setIsPending] = useState(false);
  const navigate = useNavigate();

  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  const fetchPendingDocs = async () => {
    try {
      const authToken = localStorage.getItem("p@token");
      const response = await axios.get(`${BaseURL}/api/missing/missingdocs`, {
        headers: {
          "Content-Type": "application/json",
          "auth-token": authToken,
        },
      });

      if (response.data && response.data.PendingDoc) {
        const docs = response.data.PendingDoc.map((doc) => ({
          ...doc,
          file: null,
        }));
        setPendingDocs(docs);
        setMessage(response.data.message);

        // Initialize error state for each document
        const initialErrors = {};
        docs.forEach((doc) => {
          initialErrors[doc.Name] = "";
        });
        setErrors(initialErrors);
      } else {
        showAlert("No pending documents found", "info");
        setPendingDocs([]);
      }

      // Check if the documents are pending review
      setIsPending(response.data.isPending);
    } catch (error) {
      
    }
  };

  useEffect(() => {
    fetchPendingDocs();
  }, []); // Empty dependency array to ensure it runs only once

  const validateFiles = () => {
    const newErrors = {};
    let isValid = true;

    pendingDocs.forEach((doc) => {
      if (!fileStates[doc.Name]) {
        newErrors[doc.Name] = `Please upload ${doc.Name}`;
        isValid = false;
      } else {
        const file = fileStates[doc.Name];
        // Get file extension
        const fileExtension = file.name.split(".").pop().toLowerCase();

        // Validate file type using both MIME type and extension
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];

        if (
          !allowedTypes.includes(file.type) &&
          !allowedExtensions.includes(fileExtension)
        ) {
          newErrors[doc.Name] = "Only JPG, PNG, and PDF files are allowed";
          isValid = false;
        }
        // Validate file size (max 5MB)
        else if (file.size > 5 * 1024 * 1024) {
          newErrors[doc.Name] = "File size should be less than 5MB";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleFileChange = (itemName, file) => {
    if (file) {
      // Get file extension
      const fileExtension = file.name.split(".").pop().toLowerCase();

      // Validate file type using both MIME type and extension
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setErrors((prev) => ({
          ...prev,
          [itemName]: "Only JPG, PNG, and PDF files are allowed",
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [itemName]: "File size should be less than 5MB",
        }));
        return;
      }

      // Clear error when valid file is selected
      setErrors((prev) => ({
        ...prev,
        [itemName]: "",
      }));

      setFileStates((prevState) => ({
        ...prevState,
        [itemName]: file,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateFiles()) {
      showAlert("Please upload all required documents correctly", "warning");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    Object.entries(fileStates).forEach(([itemName, file]) => {
      formData.append("AdditionalDoc", file);
    });

    try {
      const authToken = localStorage.getItem("p@token");
      // First upload the documents
      const uploadResponse = await axios.post(
        `${BaseURL}/api/missing/upload-docs`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "auth-token": authToken,
          },
        }
      );

      if (uploadResponse.data.success) {
        // Update document status to pending verification
        const statusResponse = await axios.post(
          `${BaseURL}/api/missing/update-doc-status`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              "auth-token": authToken,
            },
          }
        );

        if (statusResponse.data.success) {
          showAlert(
            "Documents uploaded and pending admin verification",
            "success"
          );
          navigate("/user-dashboard/not-verify");
        } else {
          showAlert("Failed to update document status", "warning");
        }
      } else {
        showAlert(
          uploadResponse.data.message || "Failed to upload documents",
          "danger"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      showAlert(
        error.response?.data?.message || "Error uploading documents",
        "danger"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isPending) {
    return (
      <div className="flex flex-col justify-center items-center my-20">
        <div className="bg-white rounded-xl flex flex-col gap-4 justify-center items-center py-10 px-10">
          <div className="bg-gray-200 rounded-full p-8">
            <img src="../Assets/Prop/Lock.png" alt="" className="w-16" />
          </div>
          <p className="font-pop text-gray-400 text-center max-w-md">
            While your documents are being reviewed, you cannot see or use the
            app features
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center items-center bg-gray-100 py-20">
        <div className="mx-6 w-[95%] md:w-[60%] lg:w-[40%] bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-pop font-bold mb-4">
            Identity verification
          </h2>
          <p className="text-black font-light font-pop text-lg mb-2">
            {Message}
          </p>

          {pendingDocs.map((item, index) => (
            <div key={index} className="mb-4 flex flex-col gap-2">
              <div className="flex gap-2 items-center">
                <div className="bg-gray-200 rounded-full p-3">
                  <img
                    src="../Assets/Prop/IDIcon.png"
                    className="w-5 h-5"
                    alt="ID Icon"
                  />
                </div>
                <h2 className="text-2xl font-pop font-bold">{item?.Name}</h2>
              </div>

              <div className="flex flex-col w-full">
                <label
                  htmlFor={`id-file-${item.Name}`}
                  className={`flex flex-col w-full border-2 ${
                    errors[item.Name] ? "border-red-500" : "border-primarygreen"
                  } ${
                    fileStates[item.Name]
                      ? "border-solid px-6"
                      : "border-dashed justify-center items-center"
                  } rounded-lg cursor-pointer bg-transparent hover:bg-gray-100`}
                >
                  {fileStates[item.Name] ? (
                    <div className="flex flex-row gap-4 items-center justify-start py-3 text-primarygreen font-pop">
                      <ImFilesEmpty className="text-2xl" />
                      <p className="text-lg">{fileStates[item.Name]?.name}</p>
                    </div>
                  ) : (
                    <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                      <IoIosAddCircleOutline className="text-2xl" />
                      <p className="text-lg">Upload ID file</p>
                    </div>
                  )}
                  <input
                    id={`id-file-${item.Name}`}
                    type="file"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(item.Name, e.target.files[0])
                    }
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                </label>
                {errors[item.Name] && (
                  <p className="text-red-500 text-sm mt-1 ml-2">
                    {errors[item.Name]}
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-1 ml-2">
                  Accepted formats: JPG, PNG, PDF (max 5MB)
                </p>
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmit}
            disabled={
              isLoading ||
              Object.keys(errors).some((key) => errors[key]) ||
              pendingDocs.length === 0
            }
            className={`${
              isLoading ||
              Object.keys(errors).some((key) => errors[key]) ||
              pendingDocs.length === 0
                ? "opacity-50 cursor-not-allowed"
                : ""
            } my-4 w-full bg-green-500 hover:bg-transparent font-semibold hover:text-black font-pop text-xl py-3 px-4 border border-black-500 hover:border-green-500 rounded-full text-white animate__animated animate__backInUp`}
          >
            {isLoading ? "Uploading..." : "Send"}
          </button>
        </div>
      </div>
    </>
  );
}

export default UploadMissing;
